import { capture } from '@parcel/analytics/browser';
import {
  type DynamicViewDefinition,
  type TableFilter,
  type WhereDataset,
  type WhereGroup,
  isWhereGroup,
  safeParseWhere,
} from '@parcel/filters';
import { toast } from '@parcel/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import type { FilterParamEntries } from './filter-params.js';
import { parseSearchUuid } from './filter-search.js';

export const EMPTY_TABLE_FILTER: TableFilter = Object.freeze({
  q: '',
  where: Object.freeze({ combinator: 'and', rules: [] }) as unknown as WhereGroup,
}) as TableFilter;

export interface AskWhereResponse {
  where: WhereGroup;
  q: string | null;
  unmapped: string[];
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value)
        .filter((key) => key !== 'id')
        .sort()
        .map((key) => [key, canonical((value as Record<string, unknown>)[key])]),
    );
  }
  return value;
}

/** The condition as compact JSON with sorted keys, for the URL, request params and equality. */
export const whereJson = (where: WhereGroup): string => JSON.stringify(canonical(where));

export function emptyWhere(): WhereGroup {
  return { combinator: 'and', rules: [] };
}

/** The bar's chips: a root "all of" is one chip per node; an "any of" or "not" root is one group chip. */
export function barNodes(where: WhereGroup): WhereGroup['rules'] {
  if (!where.not && (where.combinator === 'and' || where.rules.length <= 1)) return where.rules;
  return [where];
}

/** The chips back as a condition; a lone group chip is the root itself, so no level is added. */
export function whereFromBarNodes(nodes: WhereGroup['rules']): WhereGroup {
  const [only] = nodes;
  if (nodes.length === 1 && only && isWhereGroup(only)) return only;
  return { combinator: 'and', rules: nodes };
}

/** The router JSON-parses search values, so `where` arrives as an object (or a string from an older link). */
function searchWhere(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** The router parses `q=2026` as a number; the Search chip is always text. */
function searchText(raw: unknown): string {
  return typeof raw === 'string' ? raw : typeof raw === 'number' ? String(raw) : '';
}

/** The table's filter from the URL; a where that no longer parses is dropped, never an error. */
export function tableFilterFromSearch(
  dataset: WhereDataset,
  search: Record<string, unknown>,
): TableFilter {
  const q = searchText(search.q);
  const raw = searchWhere(search.where);
  if (!raw || typeof raw !== 'object') return { q, where: emptyWhere() };
  const out = safeParseWhere(dataset, raw);
  return { q, where: out.ok ? out.where : emptyWhere() };
}

/** `q` and `where` for the router, which writes `where` into the URL as compact JSON. */
export function tableFilterToSearch(filter: TableFilter): { q?: string; where?: WhereGroup } {
  return {
    ...(filter.q.trim() ? { q: filter.q } : {}),
    ...(filter.where.rules.length ? { where: canonical(filter.where) as WhereGroup } : {}),
  };
}

export function tableFiltersEqual(a: TableFilter, b: TableFilter): boolean {
  return a.q.trim() === b.q.trim() && whereJson(a.where) === whereJson(b.where);
}

export function hasTableFilter(filter: TableFilter): boolean {
  return filter.q.trim().length > 0 || filter.where.rules.length > 0;
}

/** The two request params a table endpoint takes: `q` and `where`, each only when set. */
export function tableFilterEntries(filter: TableFilter): FilterParamEntries {
  const out: Array<readonly [string, string]> = [];
  if (filter.q.trim()) out.push(['q', filter.q.trim()]);
  if (filter.where.rules.length) out.push(['where', whereJson(filter.where)]);
  return out;
}

/** A saved dynamic View as the table's filter; a View in the old stored form reads as no filter. */
export function definitionToTableFilter(
  definition: DynamicViewDefinition | { q?: unknown; where?: unknown },
): TableFilter {
  const q = typeof definition.q === 'string' ? definition.q : '';
  const where = definition.where as WhereGroup | undefined;
  return { q, where: where && Array.isArray(where.rules) ? where : emptyWhere() };
}

/** Ask's rules replace the current rules on the same fields; its groups are added; its text becomes the search. */
export function applyAskWhere(current: TableFilter, result: AskWhereResponse): TableFilter {
  const asked = barNodes(result.where);
  const askFields = new Set(asked.flatMap((r) => (isWhereGroup(r) ? [] : [r.field])));
  const kept = barNodes(current.where).filter((r) => isWhereGroup(r) || !askFields.has(r.field));
  const q = result.q?.trim() ?? '';
  return { q: q || current.q, where: whereFromBarNodes([...kept, ...asked]) };
}

/** The route's `validateSearch`: keeps q, where and (optionally) a View id; record keys pass through. */
export function makeTableSearchValidator(extra: { view?: 'uuid' } = {}) {
  return (search: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const q = searchText(search.q);
    if (q) out.q = q;
    const where = searchWhere(search.where);
    if (where && typeof where === 'object') out.where = where;
    if (extra.view === 'uuid') {
      const view = parseSearchUuid(search.view);
      if (view) out.view = view;
    }
    return out;
  };
}

function countRules(where: WhereGroup): number {
  return where.rules.reduce((n, node) => n + (isWhereGroup(node) ? countRules(node) : 1), 0);
}

/** The one chip a change touched, or null for a wholesale replace (a View, Ask, Clear all). */
export function diffTableFilterChange(
  previous: TableFilter,
  next: TableFilter,
): { field: string; operator: string } | null {
  const changed: { field: string; operator: string }[] = [];
  if (previous.q.trim() !== next.q.trim()) {
    changed.push({ field: 'q', operator: next.q.trim() ? 'contains' : 'is_not_set' });
  }
  const byField = (where: WhereGroup) =>
    new Map(barNodes(where).flatMap((r) => (isWhereGroup(r) ? [] : [[r.field, r] as const])));
  const before = byField(previous.where);
  const after = byField(next.where);
  for (const field of new Set([...before.keys(), ...after.keys()])) {
    const a = before.get(field);
    const b = after.get(field);
    if (
      whereJson({ combinator: 'and', rules: a ? [a] : [] }) ===
      whereJson({ combinator: 'and', rules: b ? [b] : [] })
    )
      continue;
    changed.push({ field, operator: b ? b.operator : 'is_not_set' });
  }
  const groups = (where: WhereGroup) =>
    whereJson({ combinator: 'and', rules: barNodes(where).filter(isWhereGroup) });
  if (groups(previous.where) !== groups(next.where))
    changed.push({ field: 'group', operator: 'any_of' });
  return changed.length === 1 ? (changed[0] ?? null) : null;
}

/** URL-synced table filter for the current route; other search keys (a record deep link, ?view=) are kept. setFilter says whether it moved the URL. */
export function useTableFilterSearch(dataset: WhereDataset) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const filter = React.useMemo(() => tableFilterFromSearch(dataset, search), [dataset, search]);
  const filterRef = React.useRef(filter);
  filterRef.current = filter;
  const setFilter = React.useCallback(
    (next: TableFilter): boolean => {
      if (tableFiltersEqual(filterRef.current, next)) return false;
      // A filter past the limits would not survive the URL; keep the current one and say why.
      const checked = safeParseWhere(dataset, next.where);
      if (!checked.ok) {
        toast.error('That filter is too big to apply', { description: checked.error.message });
        return false;
      }
      const change = diffTableFilterChange(filterRef.current, next);
      if (change) {
        capture('filter_applied', {
          entity: dataset,
          field: change.field,
          operator: change.operator,
          filter_count: countRules(next.where) + (next.q.trim() ? 1 : 0),
        });
      }
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          q: undefined,
          where: undefined,
          ...tableFilterToSearch(next),
        })) as never,
        replace: true,
      });
      return true;
    },
    [navigate, dataset],
  );
  return { filter, setFilter, filterRef };
}

/** The active `view` id plus whether the URL carries its own filters, for the Views hook. */
export function useTableViewSearchParam() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const activeViewId = parseSearchUuid(search.view) ?? null;
  const setActiveViewId = React.useCallback(
    (viewId: string | null) =>
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          view: viewId ?? undefined,
        })) as never,
        replace: true,
      }),
    [navigate],
  );
  const where = searchWhere(search.where) as { rules?: unknown } | undefined;
  const urlHasFilterKeys =
    searchText(search.q).trim().length > 0 ||
    (Array.isArray(where?.rules) && where.rules.length > 0);
  return { activeViewId, setActiveViewId, urlHasFilterKeys };
}
